import React, { useState } from 'react';
import { SnowflakeIcon, SettingsIcon, FileTextIcon, SendIcon, PlusIcon, WifiIcon, EyeIcon, EyeOffIcon, LandmarkIcon, ChevronRightIcon, CircleCheckIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { BottomNav, NavTarget } from './BottomNav';
type CardItem = {
  id: string;
  brand: 'RDB' | 'BOC' | 'Commercial' | 'Sampath';
  type: 'Debit' | 'Credit' | 'Prepaid';
  network: 'VISA' | 'MASTER' | 'AMEX';
  last4: string;
  name: string;
  expiry: string;
  active: boolean;
  gradient: string;
  accent: string;
};
const MY_CARDS: CardItem[] = [{
  id: 'c1',
  brand: 'RDB',
  type: 'Debit',
  network: 'VISA',
  last4: '6780',
  name: 'KASUN PERERA',
  expiry: '08/28',
  active: true,
  gradient: 'linear-gradient(135deg, #009AC7 0%, #0078A0 55%, #00516E 100%)',
  accent: '#FFFFFF'
}, {
  id: 'c2',
  brand: 'RDB',
  type: 'Credit',
  network: 'MASTER',
  last4: '4412',
  name: 'KASUN PERERA',
  expiry: '11/27',
  active: true,
  gradient: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 55%, #0A0A0A 100%)',
  accent: '#FACC15'
}];
const OTHER_BANK_CARDS: CardItem[] = [{
  id: 'o1',
  brand: 'Commercial',
  type: 'Credit',
  network: 'VISA',
  last4: '2245',
  name: 'KASUN PERERA',
  expiry: '06/26',
  active: true,
  gradient: 'linear-gradient(135deg, #16A34A 0%, #0F7A37 55%, #064E25 100%)',
  accent: '#FFFFFF'
}, {
  id: 'o2',
  brand: 'Sampath',
  type: 'Debit',
  network: 'MASTER',
  last4: '8830',
  name: 'KASUN PERERA',
  expiry: '04/29',
  active: false,
  gradient: 'linear-gradient(135deg, #6366F1 0%, #4338CA 55%, #2A1F9C 100%)',
  accent: '#FFFFFF'
}];
export function CardsScreen({
  onNavigate
}: {
  onNavigate?: (t: NavTarget) => void;
}) {
  const [tab, setTab] = useState<'mine' | 'other'>('mine');
  const cards = tab === 'mine' ? MY_CARDS : OTHER_BANK_CARDS;
  const [activeIdx, setActiveIdx] = useState(0);
  const [showNumber, setShowNumber] = useState(false);
  const active = cards[Math.min(activeIdx, cards.length - 1)];
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-151">
      <StatusBar data-id="element-152" />

      {/* Title bar */}
      <div className="flex items-center justify-between px-5 pt-2 pb-3" data-id="element-153">
        <h1 className="tracking-tight" style={{
        fontSize: 22,
        fontWeight: 700,
        color: '#1A1A1A',
        lineHeight: 1.2
      }} data-id="element-154">
          My Cards
        </h1>
        <button type="button" aria-label="Add card" className="flex items-center justify-center rounded-full bg-white transition-colors active:bg-neutral-100" style={{
        width: 40,
        height: 40,
        border: '1px solid #E5EAF0',
        color: '#009AC7'
      }} data-id="element-155">
          <PlusIcon size={20} strokeWidth={2.5} data-id="element-156" />
        </button>
      </div>

      {/* Segmented control */}
      <div className="px-5 mb-4" data-id="element-157">
        <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl" style={{
        backgroundColor: '#E5EAF0'
      }} data-id="element-158">
          <SegTab active={tab === 'mine'} label="RDB Cards" onClick={() => {
          setTab('mine');
          setActiveIdx(0);
        }} data-id="element-159" />
          <SegTab active={tab === 'other'} label="Other Bank Cards" onClick={() => {
          setTab('other');
          setActiveIdx(0);
        }} data-id="element-160" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-3" data-id="element-161">
        {/* Card carousel — horizontal */}
        <div className="flex gap-3 overflow-x-auto -mx-5 px-5 pb-2 mb-3 snap-x snap-mandatory" style={{
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-162">
          {cards.map((card, i) => <div key={card.id} onClick={() => setActiveIdx(i)} className="shrink-0 snap-center cursor-pointer" style={{
          width: 320
        }} data-id="element-163">
              <CardVisual card={card} showNumber={showNumber} data-id="element-164" />
            </div>)}
        </div>

        {/* Active status + show toggle */}
        <div className="flex items-center justify-between mb-4 px-1" data-id="element-165">
          <span className="inline-flex items-center gap-1.5 rounded-full" style={{
          padding: '5px 10px',
          backgroundColor: active.active ? '#ECFDF5' : '#FEF2F2',
          color: active.active ? '#059669' : '#DC2626',
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '0.02em'
        }} data-id="element-166">
            <span className="inline-block rounded-full" style={{
            width: 7,
            height: 7,
            backgroundColor: active.active ? '#10B981' : '#EF4444',
            boxShadow: active.active ? '0 0 0 3px rgba(16,185,129,0.18)' : 'none'
          }} data-id="element-167" />
            {active.active ? 'ACTIVATED' : 'FROZEN'}
          </span>
          <button type="button" onClick={() => setShowNumber(s => !s)} className="inline-flex items-center gap-1.5" style={{
          fontSize: 12,
          fontWeight: 700,
          color: '#009AC7'
        }} data-id="element-168">
            {showNumber ? <EyeOffIcon size={14} data-id="element-169" /> : <EyeIcon size={14} data-id="element-170" />}
            {showNumber ? 'Hide number' : 'Show number'}
          </button>
        </div>

        {/* Quick card actions */}
        <div className="grid grid-cols-4 gap-2.5 mb-5" data-id="element-171">
          <CardAction icon={<SnowflakeIcon size={20} data-id="element-173" />} label={active.active ? 'Freeze' : 'Unfreeze'} tint="#009AC7" data-id="element-172" />
          <CardAction icon={<SendIcon size={20} data-id="element-175" />} label="Pay" tint="#10B981" data-id="element-174" />
          <CardAction icon={<FileTextIcon size={20} data-id="element-177" />} label="Statement" tint="#8B5CF6" data-id="element-176" />
          <CardAction icon={<SettingsIcon size={20} data-id="element-179" />} label="Settings" tint="#F59E0B" data-id="element-178" />
        </div>

        {/* Other bank cards — preview when on "mine" tab */}
        {tab === 'mine' && <>
            <SectionHeading title="Linked from other banks" action="View all" onAction={() => setTab('other')} data-id="element-180" />
            <div className="flex flex-col gap-2.5 mb-3" data-id="element-181">
              {OTHER_BANK_CARDS.map(card => <LinkedRow key={card.id} card={card} data-id="element-182" />)}
            </div>
          </>}

        {/* Add card CTA */}
        <button type="button" className="w-full flex items-center gap-3 rounded-2xl bg-white px-4 py-3.5 text-left transition-all active:scale-[0.99]" style={{
        border: '1.5px dashed #009AC7'
      }} data-id="element-183">
          <span className="flex items-center justify-center rounded-xl" style={{
          width: 40,
          height: 40,
          backgroundColor: '#E6F7FB',
          color: '#009AC7'
        }} data-id="element-184">
            <PlusIcon size={20} strokeWidth={2.5} data-id="element-185" />
          </span>
          <div className="flex-1 min-w-0" data-id="element-186">
            <div style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: '#1A1A1A'
          }} data-id="element-187">
              {tab === 'mine' ? 'Apply for a new RDB card' : 'Link another bank card'}
            </div>
            <div className="mt-0.5" style={{
            fontSize: 11.5,
            color: '#64748B',
            fontWeight: 500
          }} data-id="element-188">
              {tab === 'mine' ? 'Debit, Credit, or Travel cards available' : 'Securely add cards from BOC, Sampath, HNB & more'}
            </div>
          </div>
          <ChevronRightIcon size={18} style={{
          color: '#94A3B8'
        }} data-id="element-189" />
        </button>
      </div>

      <BottomNav active="cards" onNavigate={onNavigate} data-id="element-190" />
    </div>;
}
function SegTab({
  active,
  label,
  onClick
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="rounded-xl transition-all" style={{
    minHeight: 40,
    backgroundColor: active ? '#FFFFFF' : 'transparent',
    color: active ? '#1A1A1A' : '#64748B',
    fontSize: 13,
    fontWeight: 700,
    boxShadow: active ? '0 2px 6px rgba(15,23,42,0.08)' : 'none'
  }} data-id="element-191">
      {label}
    </button>;
}
function CardVisual({
  card,
  showNumber
}: {
  card: CardItem;
  showNumber: boolean;
}) {
  return <div className="relative w-full overflow-hidden rounded-2xl" style={{
    aspectRatio: '1.6 / 1',
    background: card.gradient,
    color: card.accent,
    boxShadow: '0 20px 40px -16px rgba(15,23,42,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
  }} data-id="element-192">
      {/* Abstract highlight */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-8 rounded-full blur-2xl" style={{
      width: 180,
      height: 180,
      background: 'radial-gradient(circle, rgba(255,255,255,0.28) 0%, transparent 70%)'
    }} data-id="element-193" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -right-10 rounded-full blur-2xl" style={{
      width: 200,
      height: 200,
      background: 'radial-gradient(circle, rgba(0,0,0,0.35) 0%, transparent 70%)'
    }} data-id="element-194" />

      <div className="relative h-full w-full p-4 flex flex-col" data-id="element-195">
        {/* Top row: brand + contactless */}
        <div className="flex items-start justify-between" data-id="element-196">
          <div data-id="element-197">
            <div style={{
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: '0.06em'
          }} data-id="element-198">
              {card.brand}
            </div>
            <div className="mt-0.5" style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.18em',
            opacity: 0.8
          }} data-id="element-199">
              {card.type.toUpperCase()}
            </div>
          </div>
          <WifiIcon size={20} style={{
          color: card.accent,
          transform: 'rotate(90deg)',
          opacity: 0.85
        }} data-id="element-200" />
        </div>

        {/* Chip */}
        <div className="mt-3 flex items-center gap-2" data-id="element-201">
          <span className="rounded-md flex items-center justify-center" style={{
          width: 38,
          height: 28,
          background: 'linear-gradient(135deg, #FDE68A 0%, #D4A537 60%, #8C6B14 100%)'
        }} aria-hidden="true" data-id="element-202">
            <span className="rounded-sm" style={{
            width: 24,
            height: 16,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 60%)',
            border: '0.5px solid rgba(0,0,0,0.25)'
          }} data-id="element-203" />
          </span>
        </div>

        {/* Number */}
        <div className="mt-3 tracking-[0.18em]" style={{
        fontSize: 16,
        fontWeight: 700
      }} data-id="element-204">
          {showNumber ? `4521 8842 1130 ${card.last4}` : `•••• •••• •••• ${card.last4}`}
        </div>

        {/* Bottom row */}
        <div className="mt-auto flex items-end justify-between" data-id="element-205">
          <div data-id="element-206">
            <div style={{
            fontSize: 9,
            opacity: 0.7,
            letterSpacing: '0.12em',
            fontWeight: 600
          }} data-id="element-207">
              CARDHOLDER
            </div>
            <div className="mt-0.5" style={{
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '0.04em'
          }} data-id="element-208">
              {card.name}
            </div>
          </div>
          <div className="text-right" data-id="element-209">
            <div style={{
            fontSize: 9,
            opacity: 0.7,
            letterSpacing: '0.12em',
            fontWeight: 600
          }} data-id="element-210">
              VALID THRU
            </div>
            <div className="mt-0.5 tracking-tight" style={{
            fontSize: 12.5,
            fontWeight: 700
          }} data-id="element-211">
              {card.expiry}
            </div>
          </div>
          <NetworkLogo network={card.network} data-id="element-212" />
        </div>
      </div>
    </div>;
}
function NetworkLogo({
  network
}: {
  network: CardItem['network'];
}) {
  if (network === 'VISA') {
    return <span style={{
      fontFamily: '"SF Pro Display", system-ui, sans-serif',
      fontSize: 18,
      fontWeight: 900,
      letterSpacing: '0.02em',
      fontStyle: 'italic'
    }} data-id="element-213">
        VISA
      </span>;
  }
  if (network === 'MASTER') {
    return <span className="relative inline-block" style={{
      width: 36,
      height: 22
    }} data-id="element-214">
        <span className="absolute left-0 top-0 rounded-full" style={{
        width: 22,
        height: 22,
        backgroundColor: '#EB001B'
      }} data-id="element-215" />
        <span className="absolute right-0 top-0 rounded-full mix-blend-multiply" style={{
        width: 22,
        height: 22,
        backgroundColor: '#F79E1B'
      }} data-id="element-216" />
      </span>;
  }
  return null;
}
function CardAction({
  icon,
  label,
  tint
}: {
  icon: React.ReactNode;
  label: string;
  tint: string;
}) {
  return <button type="button" className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-white transition-all active:scale-[0.96]" style={{
    minHeight: 76,
    border: '1px solid #EEF1F5',
    padding: '10px 6px'
  }} data-id="element-217">
      <span className="flex items-center justify-center rounded-xl" style={{
      width: 40,
      height: 40,
      backgroundColor: `${tint}18`,
      color: tint
    }} data-id="element-218">
        {icon}
      </span>
      <span className="text-center" style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#1A1A1A',
      lineHeight: 1.2
    }} data-id="element-219">
        {label}
      </span>
    </button>;
}
function LinkedRow({
  card
}: {
  card: CardItem;
}) {
  return <button type="button" className="w-full flex items-center gap-3 rounded-2xl bg-white px-3.5 py-3 text-left transition-colors active:bg-neutral-50" style={{
    border: '1px solid #E5EAF0'
  }} data-id="element-220">
      <span className="shrink-0 rounded-xl overflow-hidden" style={{
      width: 54,
      height: 34,
      background: card.gradient
    }} data-id="element-221" />
      <div className="flex-1 min-w-0" data-id="element-222">
        <div className="flex items-center gap-1.5" style={{
        fontSize: 13.5,
        fontWeight: 700,
        color: '#1A1A1A'
      }} data-id="element-223">
          {card.brand} {card.type}
          {card.active && <CircleCheckIcon size={13} style={{
          color: '#10B981'
        }} data-id="element-224" />}
        </div>
        <div className="mt-0.5" style={{
        fontSize: 11.5,
        color: '#64748B',
        fontWeight: 500
      }} data-id="element-225">
          •••• {card.last4} · expires {card.expiry}
        </div>
      </div>
      <span className="rounded-full inline-flex items-center gap-1.5" style={{
      padding: '4px 8px',
      fontSize: 10.5,
      fontWeight: 700,
      backgroundColor: card.active ? '#ECFDF5' : '#FEF2F2',
      color: card.active ? '#059669' : '#DC2626',
      letterSpacing: '0.04em'
    }} data-id="element-226">
        {card.active ? 'ACTIVE' : 'INACTIVE'}
      </span>
    </button>;
}
function SectionHeading({
  title,
  action,
  onAction
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) {
  return <div className="flex items-center justify-between mb-2.5 px-0.5" data-id="element-227">
      <span style={{
      fontSize: 13,
      fontWeight: 700,
      color: '#1A1A1A'
    }} data-id="element-228">
        {title}
      </span>
      {action && <button type="button" onClick={onAction} style={{
      fontSize: 11.5,
      fontWeight: 700,
      color: '#009AC7'
    }} data-id="element-229">
          {action}
        </button>}
    </div>;
}